import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Button from "../common/Button";
import { getImageUrl } from "../../utils/imageUrl";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/api";

export default function RoomCard({ room, onDelete }) {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleChat = () => {
    if (!user) {
      const redirectURL = `/messages?withUserId=${room.ownerId}&listingId=${room.id}`;

      navigate(`/login?redirect=${encodeURIComponent(redirectURL)}`);
      return;
    }

    navigate(`/messages?withUserId=${room.ownerId}&listingId=${room.id}`);
  };

  const handleEdit = () => {
    if (!user) {
      const redirectURL = `/add-listing/${room.id}/edit`;

      navigate(`/login?redirect=${encodeURIComponent(redirectURL)}`);
      return;
    }

    if (!["ADMIN", "OWNER"].includes(user.role)) {
      toast.error("You are not authorized to edit this listing.");
      return;
    }

    navigate(`/add-listing/${room.id}/edit`);
  };

  const handleDelete = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (user.role !== "ADMIN") {
      toast.error("Only administrators can delete listings.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${room.title}"?\n\nThis action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await api.delete(`/listings/${room.id}`);

      toast.success("Listing deleted successfully.");

      if (onDelete) {
        onDelete(room.id);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);

      toast.error(err?.response?.data?.message || "Failed to delete listing.");
    }
  };

  return (
    <div
      className="
        group
        relative
        overflow-hidden
        rounded-3xl
        bg-white
        border
        border-gray-200
        shadow-md
        transition-all
        duration-500
        ease-out
        hover:-translate-y-4
        hover:scale-[1.03]
        hover:shadow-[0_30px_70px_rgba(0,0,0,0.18)]
        hover:border-orange-300
        hover:ring-2
        hover:ring-orange-200
      "
      style={{
        transformStyle: "preserve-3d",
        perspective: "1200px",
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden">
        <img
          src={getImageUrl(room.image)}
          alt={room.title}
          className="
            w-full
            h-56
            object-cover
            transition-transform
            duration-700
            group-hover:scale-110
          "
        />

        {/* Dark Overlay */}
        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/50
            via-transparent
            to-transparent
            opacity-0
            group-hover:opacity-100
            transition
            duration-500
          "
        />

        {/* Room Type Badge */}
        <div className="absolute top-4 left-4">
          <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
            {room.roomType}
          </span>
        </div>

        {/* Price */}
        <div className="absolute bottom-4 right-4">
          <span className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-full font-bold text-orange-600 shadow">
            Rs. {room.price}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div
        className="p-5 transition-all duration-500"
        style={{
          transform: "translateZ(30px)",
        }}
      >
        <h3 className="text-xl font-bold text-gray-800 line-clamp-1">
          {room.title}
        </h3>

        <p className="text-gray-500 mt-2 line-clamp-2">{room.location}</p>

        {/* Divider */}
        <div className="border-t border-gray-100 my-5"></div>

        {/* Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => navigate(`/room/${room.id}`)}
            className="flex-1 transition-transform duration-300 hover:scale-105"
          >
            View Details
          </Button>

          {room.ownerId && (
            <Button
              variant="outline"
              onClick={handleChat}
              className="transition-transform duration-300 hover:scale-105"
            >
              Chat
            </Button>
          )}

          {["ADMIN", "OWNER"].includes(user?.role) && (
            <Button
              variant="outline"
              onClick={handleEdit}
              className="transition-transform duration-300 hover:scale-105"
            >
              Edit
            </Button>
          )}

          {user?.role === "ADMIN" && (
            <Button
              onClick={handleDelete}
              className="
                bg-red-600
                hover:bg-red-700
                text-white
                transition-transform
                duration-300
                hover:scale-105
              "
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Shine Effect */}
      <div
        className="
          pointer-events-none
          absolute
          inset-0
          opacity-0
          group-hover:opacity-100
          transition
          duration-700
        "
      >
        <div
          className="
            absolute
            -left-40
            top-0
            h-full
            w-24
            rotate-12
            bg-white/30
            blur-xl
            animate-[shine_1.3s_linear]
          "
        />
      </div>
    </div>
  );
}
